import React from 'react';
import { Link } from 'react-router-dom';
import {
  SiteShell, G, H1, P, PK, INK, SUB, LINE, CARD, BG, BG2, GRAD, GLOW,
} from '../site/shell';
import { PRODUCTS } from './ProductPages';

const wrap = { maxWidth:1120, margin:'0 auto' };
const sec  = { padding:'clamp(56px,8vw,96px) clamp(20px,5vw,48px)' };
const h2s  = { ...H1, fontSize:'clamp(28px,4.4vw,44px)', lineHeight:1.08, color:INK };

/* ── Inline SVG icons (no emoji) ──────────────────────────────────── */
const sv = { width:26, height:26, viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:1.7, strokeLinecap:'round', strokeLinejoin:'round' };
const IconUnity   = () => <svg {...sv}><rect x="2.5" y="6" width="19" height="12" rx="6"/><path d="M7 10v4M5 12h4"/><circle cx="15.5" cy="11" r="1.1"/><circle cx="18" cy="13.5" r="1.1"/></svg>;
const IconCube    = () => <svg {...sv}><path d="M12 2.5 3.5 7v10L12 21.5 20.5 17V7z"/><path d="M3.5 7 12 11.5 20.5 7M12 11.5v10"/></svg>;
const IconBolt    = () => <svg {...sv}><path d="M13 2 4.5 13.5H11l-1 8.5L19.5 10H13z"/></svg>;
const IconSliders = () => <svg {...sv}><line x1="4" y1="8" x2="20" y2="8"/><line x1="4" y1="16" x2="20" y2="16"/><circle cx="9" cy="8" r="2.3"/><circle cx="15" cy="16" r="2.3"/></svg>;
const IconShield  = () => <svg {...sv}><path d="M12 3l7 3v5c0 4.6-3.1 7.7-7 9-3.9-1.3-7-4.4-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg>;
const IconLog     = () => <svg {...sv}><circle cx="4.5" cy="7" r="1.1"/><circle cx="4.5" cy="12" r="1.1"/><circle cx="4.5" cy="17" r="1.1"/><line x1="9" y1="7" x2="20" y2="7"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="17" x2="20" y2="17"/></svg>;

/* Three audiences */
const BLOCKS = [
  { Icon:IconUnity, k:'Unity', badge:'Developers', bc:P,
    t:'Tools for Unity',
    d:'Server-authoritative multiplayer systems over Mirror — procedural worlds and inventory, ready to drop into your project. No Photon, no networking from scratch.',
    to:'/produtos', cta:'See Unity tools' },
  { Icon:IconCube, k:'Minecraft', badge:'Server owners', bc:PK,
    t:'Plugins for Minecraft',
    d:'Plugins configured through a GUI and commands, without hand-editing YAML files. Install, open the menu and tweak — the server does the rest.',
    to:'/produtos', cta:'See Minecraft plugins' },
  { Icon:IconBolt, k:'Custom', badge:'Projects', bc:'#22C55E',
    t:'Custom work',
    d:'Custom Minecraft plugins and Unity multiplayer, built for your case. Brief, quote and three steps until it lands in your project.',
    to:'/servicos', cta:'See services' },
];

const PROMISE = [
  { Icon:IconSliders, t:'GUI configuration', d:'Everything wires up through an interface — the inspector in Unity, a menu in-game. Never through files or lines of code.' },
  { Icon:IconShield,  t:'Server-authoritative', d:'The server is in charge. Validation on the right side, for multiplayer that survives cheating and bad connections.' },
  { Icon:IconLog,     t:'Public changelog', d:'Every version logged by date, with full documentation. You know exactly what you get before you buy.' },
];

const TECH = ['Unity','Mirror','Paper','Spigot','PlaceholderAPI','Server-authoritative','C#','Java 21','URP','SyncLists','Self-hosted','No Photon'];

/* Hero visual: real product covers, floating */
const HERO_CARDS = [
  { slug:'unity-terraflux2d',      img:'/coverterraflux.webp', style:{ top:'2%',  left:'0%',   width:'60%', '--r':'-3deg' }, delay:'0s' },
  { slug:'minecraft-cytags',       img:'/covercytag.png',      style:{ top:'34%', right:'-2%', width:'52%', '--r':'3.5deg' }, delay:'-2.3s' },
  { slug:'unity-netinventory-pro', img:'/coverinvenotry.webp', style:{ bottom:'0%', left:'8%', width:'50%', '--r':'-2deg' }, delay:'-4.6s' },
];
const bySlug = (s) => PRODUCTS.find(p => p.slug === s) || {};

export default function LandingPage() {
  return (
    <SiteShell active="">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <header style={{ position:'relative',overflow:'hidden',padding:'clamp(116px,14vw,150px) clamp(20px,5vw,48px) clamp(40px,6vw,68px)' }}>
        <div className="glowA" style={{ position:'absolute',top:'-20%',right:'-6%',width:820,height:820,background:'radial-gradient(circle,rgba(163,102,255,.22) 0%,transparent 62%)',pointerEvents:'none' }} />
        <div className="glowB" style={{ position:'absolute',top:'20%',left:'-10%',width:560,height:560,background:'radial-gradient(circle,rgba(242,12,201,.14) 0%,transparent 62%)',pointerEvents:'none' }} />
        <div className="hero2" style={{ ...wrap, position:'relative', zIndex:1 }}>
          <div>
            <div className="hero-r" style={{ marginBottom:22,animationDelay:'.04s' }}>
              <span className="tag"><span className="dotB" style={{ width:5,height:5,borderRadius:'50%',background:P,boxShadow:`0 0 8px ${P}` }} />Tooling studio · Portugal</span>
            </div>
            <h1 className="hero-r" style={{ ...H1,fontSize:'clamp(38px,5.6vw,66px)',lineHeight:1.02,color:INK,animationDelay:'.12s' }}>
              Multiplayer and server,<br /><G>configured without code.</G>
            </h1>
            <p className="hero-r" style={{ fontSize:'clamp(16px,1.7vw,19px)',color:SUB,lineHeight:1.62,fontWeight:500,maxWidth:520,marginTop:22,animationDelay:'.24s' }}>
              Tools for <strong style={{ color:INK }}>Unity developers</strong> and <strong style={{ color:INK }}>Minecraft server owners</strong>. Everything wires up through an interface — never through files or lines of code.
            </p>
            <div className="hero-r" style={{ display:'flex',gap:12,flexWrap:'wrap',marginTop:32,animationDelay:'.34s' }}>
              <Link to="/produtos" className="btn-p">View products →</Link>
              <Link to="/servicos" className="btn-g">Custom work</Link>
            </div>
          </div>

          {/* floating real product covers */}
          <div className="hero-visual hero-r" style={{ animationDelay:'.4s' }}>
            {HERO_CARDS.map((c) => {
              const p = bySlug(c.slug);
              return (
                <Link key={c.slug} to={`/produtos/${c.slug}`} className="hcard fl2" style={{ ...c.style, aspectRatio:'16/10', animationDelay:c.delay }}>
                  <img src={c.img} alt={p.name} loading="eager" />
                  <div className="cap">
                    <span style={{ ...H1,fontSize:14,color:'#fff' }}>{p.name}</span>
                    {p.price && <span style={{ fontSize:12,fontWeight:700,color:'#fff',background:'rgba(163,102,255,.85)',padding:'3px 9px',borderRadius:999 }}>{p.price}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* tech marquee */}
        <div className="mq" style={{ ...wrap, marginTop:'clamp(40px,6vw,72px)', position:'relative', zIndex:1 }}>
          <div className="mq-row">
            {[...TECH, ...TECH].map((t,i) => <span className="mq-item" key={i}><i />{t}</span>)}
          </div>
        </div>
      </header>

      {/* ── FEATURED PRODUCTS (real images) ──────────────────── */}
      <section style={{ ...sec, background:BG2, borderTop:`1px solid ${LINE}`, borderBottom:`1px solid ${LINE}` }}>
        <div style={wrap}>
          <div className="reveal" style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:16,flexWrap:'wrap',marginBottom:32 }}>
            <div>
              <span className="tag" style={{ marginBottom:16,display:'inline-flex' }}>Shipping now</span>
              <h2 style={h2s}>The <G>products.</G></h2>
            </div>
            <Link to="/produtos" className="btn-g">All products →</Link>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:18 }}>
            {PRODUCTS.map((p,i) => {
              const live = p.status === 'live';
              return (
                <Link key={p.slug} to={`/produtos/${p.slug}`} className="work reveal" style={{ textDecoration:'none',background:CARD,border:`1px solid ${LINE}`,borderRadius:20,overflow:'hidden',display:'flex',flexDirection:'column',transitionDelay:`${i*70}ms` }}>
                  <div style={{ position:'relative',aspectRatio:'16/9',overflow:'hidden',background:BG }}>
                    <img src={p.img} alt={p.name} loading="lazy" style={{ position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover' }} />
                    <span style={{ position:'absolute',top:12,left:12,fontSize:11,fontWeight:700,color:'#fff',background:p.accent,padding:'4px 10px',borderRadius:999 }}>{p.platform}</span>
                  </div>
                  <div style={{ padding:'20px 22px 22px',display:'flex',flexDirection:'column',flex:1 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:8 }}>
                      <span style={{ ...H1,fontSize:18,color:INK }}>{p.name}</span>
                      <span style={{ marginLeft:'auto',fontSize:11,fontWeight:600,color:live?'#22C55E':'#E0A82E' }}>{live?'Available':'Coming soon'}</span>
                    </div>
                    <p style={{ fontSize:14,color:SUB,lineHeight:1.6,fontWeight:500,marginBottom:16,flex:1 }}>{p.tagline}</p>
                    <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                      <span style={{ fontSize:14,fontWeight:600,color:P }}>View →</span>
                      {p.price && <span style={{ marginLeft:'auto',fontSize:13,fontWeight:700,color:INK,background:'rgba(163,102,255,.12)',border:`1px solid ${LINE}`,padding:'3px 10px',borderRadius:999 }}>{p.price}</span>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── THREE AUDIENCES ──────────────────────────────────── */}
      <section style={sec}>
        <div style={{ ...wrap, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:18 }}>
          {BLOCKS.map((b,i) => (
            <Link key={b.k} to={b.to} className="svc reveal" style={{ textDecoration:'none',background:CARD,border:`1px solid ${LINE}`,borderRadius:22,padding:'30px 28px 26px',display:'flex',flexDirection:'column',transitionDelay:`${i*80}ms` }}>
              <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:18 }}>
                <div className="iconTile" style={{ color:b.bc }}><b.Icon /></div>
                <span style={{ fontSize:11,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:b.bc,background:`${b.bc}1c`,padding:'5px 11px',borderRadius:999 }}>{b.badge}</span>
              </div>
              <h3 style={{ ...H1,fontSize:21,color:INK,marginBottom:10 }}>{b.t}</h3>
              <p style={{ fontSize:14.5,color:SUB,lineHeight:1.7,fontWeight:500,marginBottom:20,flex:1 }}>{b.d}</p>
              <span style={{ fontSize:14,fontWeight:600,color:P }}>{b.cta} →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── BRAND PROMISE ────────────────────────────────────── */}
      <section style={{ ...sec, background:BG2, borderTop:`1px solid ${LINE}`, borderBottom:`1px solid ${LINE}` }}>
        <div style={wrap}>
          <div className="reveal" style={{ maxWidth:640,marginBottom:44 }}>
            <span className="tag" style={{ marginBottom:18,display:'inline-flex' }}>The promise</span>
            <h2 style={h2s}>No files. <G>No code.</G></h2>
            <p style={{ fontSize:16,color:SUB,lineHeight:1.7,fontWeight:500,marginTop:16 }}>
              People who buy tools spend the day in IDEs and server panels. You don't need one more config file — you need something that works the moment you plug it in.
            </p>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:16 }}>
            {PROMISE.map((p,i) => (
              <div key={p.t} className="reveal" style={{ background:CARD,border:`1px solid ${LINE}`,borderRadius:18,padding:'26px 24px',transitionDelay:`${i*80}ms` }}>
                <div className="iconTile" style={{ color:P,marginBottom:14 }}><p.Icon /></div>
                <p style={{ ...H1,fontSize:18,color:INK,marginBottom:8 }}>{p.t}</p>
                <p style={{ fontSize:14,color:SUB,lineHeight:1.7,fontWeight:500 }}>{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section style={{ padding:'clamp(56px,9vw,110px) clamp(20px,5vw,48px)' }}>
        <div className="reveal" style={{ ...wrap,borderRadius:26,background:GRAD,padding:'clamp(48px,7vw,80px) clamp(28px,5vw,56px)',textAlign:'center',position:'relative',overflow:'hidden',boxShadow:`0 24px 70px ${GLOW}` }}>
          <div className="glowA" style={{ position:'absolute',top:'-40%',left:'22%',width:460,height:460,background:'radial-gradient(circle,rgba(255,255,255,.20) 0%,transparent 60%)',pointerEvents:'none' }} />
          <div style={{ position:'relative',zIndex:1 }}>
            <h2 style={{ ...H1,fontSize:'clamp(28px,4.6vw,46px)',color:'#fff',marginBottom:14,lineHeight:1.05 }}>See the tools.</h2>
            <p style={{ fontSize:16,color:'rgba(255,255,255,.9)',fontWeight:500,maxWidth:480,margin:'0 auto 30px',lineHeight:1.65 }}>Unity multiplayer and Minecraft plugins, ready to plug in. Need something custom? Let's talk.</p>
            <div style={{ display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap' }}>
              <Link to="/produtos" style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',height:50,padding:'0 32px',borderRadius:12,background:'#0A0A11',color:'#fff',fontWeight:600,fontSize:15,textDecoration:'none' }}>View products</Link>
              <Link to="/servicos" style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',height:50,padding:'0 28px',borderRadius:12,background:'rgba(255,255,255,.16)',border:'1px solid rgba(255,255,255,.4)',color:'#fff',fontWeight:500,fontSize:15,textDecoration:'none' }}>Custom work</Link>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
